import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import { IoMdClose } from "react-icons/io";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import Switch from "@mui/material/Switch";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import {
  AppliedTaxes,
  AppliedValues,
  OverrideValue,
  ServiceValues,
  typeTaxKeys,
  typeValueOptions,
} from "../../../../types/budget/service-item";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import { FaCheck } from "react-icons/fa";
import { Info as InfoIcon } from "@mui/icons-material";
import { hasCustomTaxes } from "../../../../utils/utils";
import { createOptionsFromEnum } from "../../../../utils/formatters";
import ChangelingMonetaryInput from "../../../UI/ChangelingMonetaryInput";
import ExchangeFields from "../../../UI/ExchangeFields";
import { toast } from "react-toastify";
import { formatCurrencyValue } from "../../../../utils/currencySymbol";
import { AuthContext } from "../../../../contexts/auth";
import {
  editBitributedRoles,
  ExchangeData,
} from "../../../../types/budget/budget";
import { EventScopeEnum } from "../../../../types/project/enums";
import {
  APPLIED_TAX_PILL_KEYS,
  isAppliedTaxPillVisible,
  resolveTaxPillCurrency,
} from "../../../../utils/appliedTaxesByCurrency";
import { areExchangesEqual } from "../../../../utils/exchangeManager";

interface TaxesModalProps {
  isOpen: boolean;
  onClose: () => void;
  serviceValues: ServiceValues;
  onChange(taxes: any, value: any): void;
  onConfirm(valuesOverride?: Partial<ServiceValues>): void;
  mainAppliedTaxes?: AppliedTaxes;
  /** Moeda da categoria (effectiveCategoryExchange), alinhada a Taxas padrão e Valores */
  exchangeCurrency?: string;
  /** Câmbio herdado da categoria — usado quando o serviço não tem override */
  categoryExchange?: ExchangeData;
  /** Escopo do evento (nacional força pills em BRL e oculta o bloco de câmbio) */
  eventScope?: string;
}

const FALLBACK_EXCHANGE: ExchangeData = {
  date: "",
  currency: "BRL",
  value: 1,
};

function resolveInheritedExchange(
  categoryExchange?: ExchangeData,
  exchangeCurrency?: string,
  serviceValues?: ServiceValues
): ExchangeData {
  if (categoryExchange?.currency) {
    return categoryExchange;
  }
  if (serviceValues?.exchangeData && !serviceValues.exchangeOverride) {
    return serviceValues.exchangeData;
  }
  return {
    date: serviceValues?.exchangeData?.date || "",
    currency:
      exchangeCurrency || serviceValues?.exchangeData?.currency || "BRL",
    value: serviceValues?.exchangeData?.value ?? 1,
  };
}

const valueTypeOptions = createOptionsFromEnum(typeValueOptions);
const percentageOnlyOptions = valueTypeOptions.filter(
  (option: any) => option.value === "PERCENTAGE"
);

const defaultSupplierValue: OverrideValue = {
  calcType: "PERCENTAGE",
  value: 0,
};

/** Markup em moeda convertida (espelha a base tarifa do cálculo principal). */
function estimateMarkupConverted(
  sv: ServiceValues,
  markupEnabled: boolean,
  mv: OverrideValue
): number {
  if (!markupEnabled) {
    return sv.taxData?.markup?.currencyValue ?? 0;
  }
  if (!mv?.calcType) {
    return 0;
  }
  const tariff = sv.taxData?.tariff?.currencyValue ?? 0;
  if (mv.calcType === "PERCENTAGE" || mv.calcType === "PERCENTAGE_RJ") {
    return ((Number(mv.value) || 0) / 100) * tariff;
  }
  if (mv.calcType === "FIX_VALUE") {
    return Number(mv.value) || 0;
  }
  return sv.taxData?.markup?.currencyValue ?? 0;
}

const TaxesModal = ({
  onClose,
  isOpen,
  serviceValues,
  onChange,
  onConfirm,
  mainAppliedTaxes,
  exchangeCurrency,
  categoryExchange,
  eventScope,
}: TaxesModalProps) => {
  const { userRoleName } = useContext(AuthContext);
  const canEditBiTributed = editBitributedRoles.includes(userRoleName);
  const lastTaxes = useRef<any | null>(null);
  const lastAppliedValues = useRef<AppliedValues | undefined>(undefined);
  const draftAppliedTaxesRef = useRef<AppliedTaxes>({} as AppliedTaxes);
  const draftAppliedValuesRef = useRef<AppliedValues>({});
  const [draftAppliedTaxes, setDraftAppliedTaxes] = useState<AppliedTaxes>(
    {} as AppliedTaxes
  );
  const [draftAppliedValues, setDraftAppliedValues] = useState<AppliedValues>(
    {}
  );
  const [markupEnabled, setMarkupEnabled] = useState(false);
  const [markupValue, setMarkupValue] = useState<OverrideValue>({
    calcType: "",
    value: 0,
  });
  const [commissionEnabled, setCommissionEnabled] = useState(false);
  const [commissionValue, setCommissionValue] = useState<OverrideValue>({
    calcType: "",
    value: 0,
  });
  const [supplierTributeValue, setSupplierTributeValue] =
    useState<OverrideValue>(defaultSupplierValue);
  const [exchangeEnabled, setExchangeEnabled] = useState(false);
  const [draftExchange, setDraftExchange] =
    useState<ExchangeData>(FALLBACK_EXCHANGE);

  const showExchangeBlock =
    eventScope === EventScopeEnum.INTERNATIONAL ||
    eventScope === EventScopeEnum.BOTH;

  const inheritedExchange = useMemo(
    () =>
      resolveInheritedExchange(
        categoryExchange,
        exchangeCurrency,
        serviceValues
      ),
    [categoryExchange, exchangeCurrency, serviceValues]
  );

  const currencyCode = resolveTaxPillCurrency(
    exchangeEnabled
      ? draftExchange.currency
      : exchangeCurrency ??
          inheritedExchange.currency ??
          serviceValues.exchangeData?.currency,
    eventScope
  );

  const hasDifferentTaxes = useMemo(() => {
    return (
      hasCustomTaxes(
        draftAppliedTaxes,
        mainAppliedTaxes,
        draftAppliedValues?.markupOverride,
        draftAppliedValues?.commissionOverride,
        Boolean(draftAppliedValues?.supplierTributeOverride) &&
          !draftAppliedValues?.markupOverride,
        // `currencyCode` já saiu de resolveTaxPillCurrency(…, eventScope): é a
        // mesma moeda usada para filtrar as pills logo abaixo.
        { currency: currencyCode }
      ) || exchangeEnabled
    );
  }, [
    draftAppliedTaxes,
    mainAppliedTaxes,
    draftAppliedValues,
    exchangeEnabled,
    currencyCode,
  ]);

  const followsCategoryExchange =
    !exchangeEnabled || areExchangesEqual(draftExchange, inheritedExchange);

  /** Mesmo critério da categoria (calcSupplierTributeHelper): markup × (1 − %/100). */
  const valorFornecedorPreview = useMemo(() => {
    if (!markupEnabled) {
      return { converted: 0, brl: 0 };
    }
    const exchange = exchangeEnabled
      ? draftExchange.value ?? 1
      : inheritedExchange.value ?? serviceValues.exchangeData?.value ?? 1;
    const markupEst = estimateMarkupConverted(
      serviceValues,
      markupEnabled,
      markupValue
    );
    const pct =
      supplierTributeValue.calcType === "PERCENTAGE"
        ? Number(supplierTributeValue.value) || 0
        : 0;
    const converted = markupEst * (1 - pct / 100);
    const brl = converted * exchange;
    return { converted, brl };
  }, [
    markupEnabled,
    markupValue,
    serviceValues,
    supplierTributeValue.calcType,
    supplierTributeValue.value,
    exchangeEnabled,
    draftExchange.value,
    inheritedExchange.value,
  ]);

  const showDualCurrencyTotals = String(currencyCode).toUpperCase() !== "BRL";

  useEffect(() => {
    if (isOpen) {
      lastTaxes.current = { ...serviceValues.appliedTaxes };
      lastAppliedValues.current = serviceValues.appliedValues
        ? { ...serviceValues.appliedValues }
        : undefined;
      draftAppliedTaxesRef.current = { ...serviceValues.appliedTaxes };
      setDraftAppliedTaxes(draftAppliedTaxesRef.current);
      const av = serviceValues.appliedValues;
      draftAppliedValuesRef.current = av ? { ...av } : {};
      setDraftAppliedValues(draftAppliedValuesRef.current);
      const mk = av?.markupOverride ?? false;
      setMarkupEnabled(mk);
      setMarkupValue(av?.markupOverrideValue ?? { calcType: "", value: 0 });
      setCommissionEnabled(av?.commissionOverride ?? false);
      setCommissionValue(
        av?.commissionOverrideValue ?? { calcType: "", value: 0 }
      );
      setSupplierTributeValue(
        mk && av?.supplierTributeOverrideValue
          ? av.supplierTributeOverrideValue
          : defaultSupplierValue
      );
      const inherited = resolveInheritedExchange(
        categoryExchange,
        exchangeCurrency,
        serviceValues
      );
      const hasOverride = Boolean(serviceValues.exchangeOverride);
      setExchangeEnabled(hasOverride);
      setDraftExchange(
        hasOverride && serviceValues.exchangeData
          ? { ...serviceValues.exchangeData }
          : { ...inherited }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const updateDraftAppliedTaxes = (nextAppliedTaxes: AppliedTaxes) => {
    draftAppliedTaxesRef.current = nextAppliedTaxes;
    setDraftAppliedTaxes(nextAppliedTaxes);
    onChange(nextAppliedTaxes, "appliedTaxes");
  };

  const updateDraftAppliedValues = (nextAppliedValues: AppliedValues) => {
    draftAppliedValuesRef.current = nextAppliedValues;
    setDraftAppliedValues(nextAppliedValues);
    onChange(nextAppliedValues, "appliedValues");
  };

  const handleClose = () => {
    onChange({ ...lastTaxes.current }, "appliedTaxes");
    onChange({ ...(lastAppliedValues.current ?? {}) }, "appliedValues");
    onClose();
  };

  const handleConfirm = () => {
    let valid = true;
    const draft = draftAppliedValuesRef.current;
    const draftMarkupValue = draft.markupOverrideValue;
    const draftCommissionValue = draft.commissionOverrideValue;

    if (
      draft.markupOverride &&
      (!draftMarkupValue?.calcType ||
        (draftMarkupValue.value !== 0 && !draftMarkupValue.value))
    ) {
      toast.error("Preencha o valor do campo Markup/Over para prosseguir.");
      valid = false;
    }
    if (
      draft.commissionOverride &&
      (!draftCommissionValue?.calcType ||
        (draftCommissionValue.value !== 0 && !draftCommissionValue.value))
    ) {
      toast.error("Preencha o valor do campo Comissão para prosseguir.");
      valid = false;
    }
    if (!valid) return;

    const appliedValues: AppliedValues = { ...draft };
    if (!appliedValues.markupOverride) {
      appliedValues.supplierTributeOverride = undefined;
      appliedValues.supplierTributeOverrideValue = undefined;
    } else {
      appliedValues.supplierTributeOverride = true;
      appliedValues.supplierTributeOverrideValue =
        appliedValues.supplierTributeOverrideValue ?? supplierTributeValue;
    }

    const exchangePayload = showExchangeBlock
      ? {
          exchangeData: exchangeEnabled
            ? { ...draftExchange }
            : { ...inheritedExchange },
          exchangeOverride: exchangeEnabled,
        }
      : {};

    onConfirm({
      appliedTaxes: { ...draftAppliedTaxesRef.current },
      appliedValues,
      ...exchangePayload,
    });
    onClose();
  };

  const handleResetToDefault = () => {
    if (mainAppliedTaxes) {
      updateDraftAppliedTaxes({ ...mainAppliedTaxes });
    } else if (lastTaxes.current) {
      updateDraftAppliedTaxes({ ...lastTaxes.current });
    }
    setMarkupEnabled(false);
    setMarkupValue({ calcType: "", value: 0 });
    setCommissionEnabled(false);
    setCommissionValue({ calcType: "", value: 0 });
    setSupplierTributeValue(defaultSupplierValue);
    updateDraftAppliedValues({});
    setExchangeEnabled(false);
    setDraftExchange({ ...inheritedExchange });
  };

  const handleResetExchangeToDefault = () => {
    setExchangeEnabled(false);
    setDraftExchange({ ...inheritedExchange });
  };

  const handleToggleServiceExchange = (enabled: boolean) => {
    setExchangeEnabled(enabled);
    if (!enabled) {
      setDraftExchange({ ...inheritedExchange });
    } else if (!draftExchange.currency) {
      setDraftExchange({ ...inheritedExchange });
    }
  };

  return (
    <Dialog onClose={handleClose} open={isOpen} fullWidth maxWidth={"md"}>
      <DialogTitle sx={{ m: 0, px: 4, color: "#4B465C" }}>
        <Box display="flex" flexDirection="column">
          <Typography variant="subtitle1" fontWeight={600}>
            Taxas
          </Typography>
          <Typography variant="subtitle2" color="text.secondary">
            As taxas selecionadas abaixo substituirão as taxas padrão neste
            serviço.
          </Typography>
          <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>
            Taxas do serviço
          </Typography>
        </Box>
      </DialogTitle>

      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: "absolute",
          right: 16,
          top: 16,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <IoMdClose />
      </IconButton>
      <DialogContent sx={{ px: 4 }}>
        <Box display={"flex"} flexDirection={"column"} gap={2}>
          <Box gap={1} display={"flex"} sx={{ flexWrap: "wrap" }}>
            {APPLIED_TAX_PILL_KEYS.filter(
              (key) =>
                (canEditBiTributed || key !== "doubleCharged") &&
                isAppliedTaxPillVisible(key, currencyCode, eventScope)
            ).map((key) => {
              const value = Boolean(draftAppliedTaxes?.[key]);
              return (
                <Chip
                  key={key}
                  variant="outlined"
                  onClick={() => {
                    return updateDraftAppliedTaxes({
                      ...draftAppliedTaxesRef.current,
                      [key]: !value,
                    });
                  }}
                  sx={{ fontSize: "12px" }}
                  avatar={
                    <Avatar sx={{ background: "#F1F2F3" }} variant="circular">
                      {value ? <FaCheck color="#1361A4" /> : " "}
                    </Avatar>
                  }
                  label={typeTaxKeys[key]}
                />
              );
            })}
          </Box>

          <Divider sx={{ mt: 1 }} />

          {/* Seção Valores personalizados */}
          <Box display="flex" flexDirection="column" gap={1.5}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Valores personalizados
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Os valores, se definidos, abaixo substituirão os valores padrão
                para este serviço.
              </Typography>
            </Box>

            <Box
              display="flex"
              flexWrap="wrap"
              gap={3}
              mt={1}
              rowGap={2}
              columnGap={3}
            >
              <Box display="flex" flexDirection="column" width={220}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography variant="body2" fontWeight={500}>
                    Markup/Over
                  </Typography>
                  <Switch
                    size="small"
                    checked={markupEnabled}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      setMarkupEnabled(enabled);
                      const base: AppliedValues = {
                        ...draftAppliedValuesRef.current,
                        markupOverride: enabled,
                        markupOverrideValue: enabled ? markupValue : undefined,
                      };
                      if (enabled) {
                        base.supplierTributeOverride = true;
                        base.supplierTributeOverrideValue =
                          draftAppliedValuesRef.current
                            .supplierTributeOverrideValue ??
                          supplierTributeValue;
                      } else {
                        base.supplierTributeOverride = undefined;
                        base.supplierTributeOverrideValue = undefined;
                      }
                      updateDraftAppliedValues(base);
                    }}
                  />
                </Box>
                <Box sx={{ width: 220 }}>
                  <ChangelingMonetaryInput
                    prevValue={markupValue}
                    label=""
                    type="MARKUP"
                    calculusOptions={valueTypeOptions}
                    updateCalcValues={(_type, value, calcType) => {
                      const newVal = { calcType, value };
                      setMarkupValue(newVal);
                      const base: AppliedValues = {
                        ...draftAppliedValuesRef.current,
                        markupOverride: markupEnabled,
                        markupOverrideValue: markupEnabled ? newVal : undefined,
                      };
                      if (markupEnabled) {
                        base.supplierTributeOverride = true;
                        base.supplierTributeOverrideValue =
                          draftAppliedValuesRef.current
                            .supplierTributeOverrideValue ??
                          supplierTributeValue;
                      }
                      updateDraftAppliedValues(base);
                    }}
                    disabled={!markupEnabled}
                  />
                </Box>
              </Box>

              <Box display="flex" flexDirection="column" width={220}>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                  Imposto sobre Markup/Over
                </Typography>
                {/* <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                  Somente com Markup/Over ativo. Percentual sobre o markup.
                </Typography> */}
                <Box sx={{ width: 220 }}>
                  <ChangelingMonetaryInput
                    prevValue={supplierTributeValue}
                    label=""
                    type="SUPPLIER_TRIBUTE"
                    calculusOptions={percentageOnlyOptions}
                    updateCalcValues={(_type, value, calcType) => {
                      const newVal = { calcType, value };
                      setSupplierTributeValue(newVal);
                      updateDraftAppliedValues({
                        ...draftAppliedValuesRef.current,
                        markupOverride: true,
                        markupOverrideValue:
                          draftAppliedValuesRef.current.markupOverrideValue ??
                          markupValue,
                        supplierTributeOverride: true,
                        supplierTributeOverrideValue: newVal,
                      } as AppliedValues);
                    }}
                    disabled={!markupEnabled}
                  />
                </Box>
              </Box>

              <Box display="flex" flexDirection="column" width={220}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography variant="body2" fontWeight={500}>
                    Comissão
                  </Typography>
                  <Switch
                    size="small"
                    checked={commissionEnabled}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      setCommissionEnabled(enabled);
                      updateDraftAppliedValues({
                        ...draftAppliedValuesRef.current,
                        commissionOverride: enabled,
                        commissionOverrideValue: enabled
                          ? commissionValue
                          : undefined,
                      } as AppliedValues);
                    }}
                  />
                </Box>
                <Box sx={{ width: 220 }}>
                  <ChangelingMonetaryInput
                    prevValue={commissionValue}
                    label=""
                    type="COMMISSION"
                    calculusOptions={valueTypeOptions}
                    updateCalcValues={(_type, value, calcType) => {
                      const newVal = { calcType, value };
                      setCommissionValue(newVal);
                      updateDraftAppliedValues({
                        ...draftAppliedValuesRef.current,
                        commissionOverride: commissionEnabled,
                        commissionOverrideValue: commissionEnabled
                          ? newVal
                          : undefined,
                      } as AppliedValues);
                    }}
                    disabled={!commissionEnabled}
                  />
                </Box>
              </Box>
            </Box>

            {markupEnabled && (
              <Box
                sx={{
                  mt: 0.5,
                  p: 1.5,
                  borderRadius: 1,
                  bgcolor: "action.hover",
                  maxWidth: 480,
                }}
              >
                <Typography variant="body2" fontWeight={600}>
                  Valor fornecedor (este serviço)
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {showDualCurrencyTotals ? (
                    <>
                      {formatCurrencyValue(
                        valorFornecedorPreview.converted,
                        currencyCode
                      )}
                      {" · "}
                      {formatCurrencyValue(valorFornecedorPreview.brl, "BRL")}
                    </>
                  ) : (
                    formatCurrencyValue(valorFornecedorPreview.brl, "BRL")
                  )}
                </Typography>
              </Box>
            )}
          </Box>

          {showExchangeBlock && (
            <Box
              display="flex"
              flexDirection="column"
              gap={1.5}
              sx={{
                border: "1px solid #B6D4F0",
                borderRadius: 1,
                p: 2,
                backgroundColor: "#FAFDFF",
              }}
            >
              <Box
                display="flex"
                alignItems="flex-start"
                justifyContent="space-between"
                gap={2}
              >
                <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Câmbio
                  </Typography>
                  <Tooltip title="Defina um câmbio específico para este serviço. Caso contrário, será utilizado o câmbio da categoria.">
                    <InfoIcon
                      fontSize="small"
                      sx={{ color: "text.secondary", cursor: "help" }}
                    />
                  </Tooltip>
                </Box>

                <Box flex={1}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Switch
                      size="small"
                      checked={exchangeEnabled}
                      onChange={(e) =>
                        handleToggleServiceExchange(e.target.checked)
                      }
                      inputProps={{
                        "aria-label":
                          "Utilizar câmbio específico para este serviço",
                      }}
                    />
                    <Typography variant="body2" fontWeight={500}>
                      Utilizar câmbio específico para este serviço
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", ml: 6 }}
                  >
                    Ao desativar, este serviço voltará a utilizar o câmbio
                    padrão da categoria.
                  </Typography>
                </Box>

                <Button
                  onClick={handleResetExchangeToDefault}
                  size="small"
                  sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                >
                  Redefinir para o padrão
                </Button>
              </Box>

              {followsCategoryExchange && (
                <Alert severity="info" icon={<InfoIcon />}>
                  Este serviço segue o câmbio padrão da categoria. Altere os
                  campos abaixo somente se este serviço tiver uma cotação
                  específica.
                </Alert>
              )}

              <ExchangeFields
                date={draftExchange.date || ""}
                currency={draftExchange.currency || "BRL"}
                value={draftExchange.value ?? 1}
                disabled={!exchangeEnabled}
                disableCurrency
                onDateChange={(date) =>
                  setDraftExchange((prev) => ({ ...prev, date }))
                }
                onExchangeChange={(currency, value, date) =>
                  setDraftExchange((prev) => ({
                    ...prev,
                    currency,
                    value,
                    ...(date !== undefined ? { date } : {}),
                  }))
                }
              />
            </Box>
          )}

          {hasDifferentTaxes && (
            <Alert severity="warning">
              As taxas e/ou valores desse serviço foram alteradas. Valores
              padrão serão desconsiderados e sobrescritos pelos valores
              definidos nesta tela.
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 4, py: 3 }}>
        <span
          style={{
            color: "#1361A4",
            cursor: "pointer",
            fontSize: 14,
            marginRight: 8,
          }}
          onClick={handleResetToDefault}
        >
          Redefinir para o padrão
        </span>
        <Button onClick={handleClose} variant="outlined">
          Cancelar
        </Button>
        <Button onClick={handleConfirm}>Salvar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaxesModal;
